import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { format } from 'date-fns';
import { type DateRange } from 'react-day-picker';
import {
  CalendarIcon,
  ChevronDown,
  ChevronRight,
  Download,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageTitle } from '@/components/page-title';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useNotification } from '@/components/notifications';
import { useGetCrossOrgCost } from '@/hooks/queries/admin/use-get-cross-org-cost';
import { useUpdateBillingConfig } from '@/hooks/mutations/admin/use-update-billing-config';
import { exportCostCsv } from '@/utils/export-cost-csv';
import type {
  OrgCost,
  ProviderCost,
} from '@/services/admin/get-cross-org-cost';

const usd = (n: number) => `$${n.toFixed(2)}`;

function formatRange(range: DateRange): string {
  if (!range.from) return 'All time';
  const from = format(range.from, 'MM/dd/yyyy');
  if (!range.to || range.from.getTime() === range.to.getTime()) return from;
  return `${from} – ${format(range.to, 'MM/dd/yyyy')}`;
}

function TotalCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader className='pb-2'>
        <CardTitle className='text-sm font-medium'>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='text-2xl font-bold'>{value}</div>
      </CardContent>
    </Card>
  );
}

function MarkupCell({
  org,
  onSave,
}: {
  org: OrgCost;
  onSave: (orgId: string, pct: number) => Promise<void>;
}) {
  const [value, setValue] = useState(String(org.markup_percentage));
  const focusedRef = useRef(false);
  const serverPctRef = useRef(org.markup_percentage);

  // Re-seed from the server value (e.g. after a refetch/mutation)
  useEffect(() => {
    serverPctRef.current = org.markup_percentage;
    if (!focusedRef.current) setValue(String(org.markup_percentage));
  }, [org.markup_percentage]);

  const commit = () => {
    const trimmed = value.trim();
    const pct = Number(trimmed);
    // Empty/whitespace or invalid input reverts to the server value rather than
    // persisting an unintended 0%.
    if (trimmed === '' || Number.isNaN(pct) || pct < 0) {
      setValue(String(org.markup_percentage));
      return;
    }
    if (pct !== org.markup_percentage) {
      // Revert the input if the save fails so it never shows an unsaved value.
      void onSave(org.org_id, pct).catch(() =>
        setValue(String(serverPctRef.current)),
      );
    }
  };

  return (
    <Input
      type='number'
      min={0}
      value={value}
      className='h-8 w-20'
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setValue(e.target.value)}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

function ProviderRows({ provider }: { provider: ProviderCost }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow
        className='cursor-pointer bg-muted/30'
        role='button'
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        <TableCell />
        <TableCell className='pl-8 text-sm'>
          <span className='inline-flex items-center gap-1'>
            {open ? (
              <ChevronDown className='size-3' />
            ) : (
              <ChevronRight className='size-3' />
            )}
            {provider.provider}
          </span>
        </TableCell>
        <TableCell />
        <TableCell className='text-right text-sm'>
          {usd(provider.base_cost_usd)}
        </TableCell>
        <TableCell className='text-right text-sm'>
          {usd(provider.marked_up_cost_usd)}
        </TableCell>
        <TableCell />
      </TableRow>
      {open &&
        provider.models.map((m) => (
          <TableRow
            key={`${provider.provider}-${m.model}`}
            className='bg-muted/10'
          >
            <TableCell />
            <TableCell className='pl-14 text-sm text-muted-foreground'>
              {m.model}
            </TableCell>
            <TableCell />
            <TableCell className='text-right text-sm text-muted-foreground'>
              {usd(m.base_cost_usd)}
            </TableCell>
            <TableCell className='text-right text-sm text-muted-foreground'>
              {usd(m.marked_up_cost_usd)}
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
    </>
  );
}

function OrgRows({
  org,
  expanded,
  onToggle,
  onSaveMarkup,
}: {
  org: OrgCost;
  expanded: boolean;
  onToggle: () => void;
  onSaveMarkup: (orgId: string, pct: number) => Promise<void>;
}) {
  const unavailable = !org.cost_available;
  const chevron = expanded ? (
    <ChevronDown className='size-4' />
  ) : (
    <ChevronRight className='size-4' />
  );
  return (
    <>
      <TableRow
        className={unavailable ? undefined : 'cursor-pointer'}
        role={unavailable ? undefined : 'button'}
        tabIndex={unavailable ? undefined : 0}
        aria-expanded={unavailable ? undefined : expanded}
        onClick={unavailable ? undefined : onToggle}
        onKeyDown={
          unavailable
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle();
                }
              }
        }
      >
        <TableCell className='w-8'>{unavailable ? null : chevron}</TableCell>
        <TableCell className='font-medium'>
          {org.org_name ?? org.org_id}
        </TableCell>
        <TableCell>
          <MarkupCell org={org} onSave={onSaveMarkup} />
        </TableCell>
        {unavailable ? (
          <TableCell
            colSpan={3}
            className='text-right text-sm text-muted-foreground'
          >
            Cost data unavailable
          </TableCell>
        ) : (
          <>
            <TableCell className='text-right'>
              {usd(org.base_cost_usd)}
            </TableCell>
            <TableCell className='text-right'>
              {usd(org.marked_up_cost_usd)}
            </TableCell>
            <TableCell className='text-right'>{usd(org.margin_usd)}</TableCell>
          </>
        )}
      </TableRow>
      {!unavailable &&
        expanded &&
        org.providers.map((p) => (
          <ProviderRows key={p.provider} provider={p} />
        ))}
    </>
  );
}

export function CostPage() {
  const notification = useNotification();
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLDivElement>(null);

  // Only query a complete range; a half-picked range (from, no to) would
  // otherwise fetch an open-ended start_date mid-selection.
  const rangeFrom = dateRange?.from;
  const rangeTo = dateRange?.to;
  const startDate =
    rangeFrom && rangeTo ? format(rangeFrom, 'yyyy-MM-dd') : undefined;
  const endDate =
    rangeFrom && rangeTo ? format(rangeTo, 'yyyy-MM-dd') : undefined;

  const { data, isLoading, error, refetch, isFetching } = useGetCrossOrgCost(
    startDate,
    endDate,
  );
  const updateMarkup = useUpdateBillingConfig();

  const toggle = (orgId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });

  const handleSaveMarkup = async (orgId: string, pct: number) => {
    try {
      await updateMarkup.mutateAsync({ orgId, markupPercentage: pct });
      notification.success('Markup updated');
    } catch (e) {
      Sentry.captureException(e, {
        extra: { context: 'updateBillingConfig', orgId },
      });
      notification.error('Failed to update markup');
      throw e; // let MarkupCell revert the input to the server value
    }
  };

  const hasRange = !!rangeFrom && !!rangeTo;

  // Disable export when there are no data rows to write (e.g. only degraded
  // orgs) — buildCostCsv would otherwise produce a header-only file.
  const canExport =
    !!data &&
    data.orgs.some(
      (o) => o.cost_available && o.providers.some((p) => p.models.length > 0),
    );

  return (
    <div className='w-full space-y-6'>
      <div className='space-y-2'>
        <PageTitle title='Cost Dashboard' />
        <p className='text-base text-muted-foreground'>
          Per-org inference cost with provider and model breakdown.
        </p>
      </div>
      <Separator />

      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='w-[220px] justify-between text-left font-normal'
              >
                {hasRange ? formatRange(dateRange!) : 'All time'}
                <CalendarIcon className='ml-2 size-4' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
              <Calendar
                mode='range'
                disabled={{ after: new Date() }}
                selected={dateRange}
                onSelect={setDateRange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {hasRange && (
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setDateRange(undefined)}
            >
              Clear
            </Button>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={cn('mr-2 size-4', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
          <Button
            variant='outline'
            size='sm'
            disabled={!canExport || isFetching}
            onClick={() => data && exportCostCsv(data)}
          >
            <Download className='mr-2 size-4' />
            Export CSV
          </Button>
        </div>
      </div>

      {error && data && (
        <p className='text-sm text-destructive'>
          Could not refresh — showing the last loaded data.
        </p>
      )}

      {data && (
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
          <TotalCard title='Base cost' value={usd(data.totals.base_cost_usd)} />
          <TotalCard
            title='Marked-up cost'
            value={usd(data.totals.marked_up_cost_usd)}
          />
          <TotalCard title='Margin' value={usd(data.totals.margin_usd)} />
        </div>
      )}

      {error && !data && (
        <p className='text-sm text-muted-foreground'>
          {error instanceof Error ? error.message : 'Failed to load cost data'}
        </p>
      )}
      {isLoading && <Skeleton className='h-64 w-full' />}
      {data && (
        <div className='rounded-md border'>
          <Table tableRef={tableRef}>
            <TableHeader>
              <TableRow>
                <TableHead className='w-8' />
                <TableHead>Organization</TableHead>
                <TableHead className='w-28'>Markup %</TableHead>
                <TableHead className='text-right'>Base</TableHead>
                <TableHead className='text-right'>Marked-up</TableHead>
                <TableHead className='text-right'>Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data && data.orgs.length > 0 ? (
                data.orgs.map((org) => (
                  <OrgRows
                    key={org.org_id}
                    org={org}
                    expanded={expanded.has(org.org_id)}
                    onToggle={() => toggle(org.org_id)}
                    onSaveMarkup={handleSaveMarkup}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className='h-24 text-center text-muted-foreground'
                  >
                    No cost data for this period.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
